import React, {useState, useEffect, useRef} from 'react';
import {useSelector} from 'react-redux';
import {isMobile} from 'react-device-detect';
import BtnSubmit from 'app/Components/Shared/Buttons/BtnSubmit';
import {dwhCreditCardPageReport, dwhUpdatePaymentMethodReport, DwhActionType, DWHMessageText, DWHStatus} from '../reports/dwhReports';
import PaymentCG from '../../SaleBlock/PaymentCG/PaymentCG';
import CheckBox from 'app/Components/Shared/CheckBox/Checkbox';
import {DebtPaymentBitWrapper} from '../DebtPaymentBitWrapper';
import BtnWhiteBlackBorderBigSize from 'app/Components/Shared/Buttons/BtnWhiteBlackBorderBigSize';
import {paymentAction, popupEnum} from '../Constants/debtPaymentEnum';
import {cgTypeEnum} from '../../../../utilities/enums/cgTypeEnum';
import {useDebtPaymentCreditCard} from '../hooks/useDebtPaymentCreditCard';
import useEnterKeyPress from '../hooks/useEnterKeyPress';
import {useDebtPaymentBit} from '../DebtPaymentBitWrapper';
import ContactUsAction from 'app/Components/Shared/ContactUs/ContactUsAction';
import {contactUsEnergyCC, contactUsEnergyMob} from '../Constants/debtPaymentEnum';
import stageConfig from 'app/StageConfig';
import {formatAmount} from '../utils/banTypeClassifier';
import UpdatePaymentBanStrip from '../SharedComponents/UpdatePaymentBanStrip';
import {useSelectedBansForUpdatePayment} from '../hooks/useSelectedBansForUpdatePayment';
import CreditCardPayingEntityBanner from '../SharedComponents/CreditCardPayingEntityBanner';
import {selectServerData, selectEpiData} from '../DebtAndUpdatePaymentBlockSlice';
import Image from 'app/Components/Shared/Image/image';
const PAYMENT_CG_IFRAME_HEIGHT = ['production', 'golan-production'].includes(stageConfig.stage) ? 400 : 470;
const PAYMENT_CG_IFRAME_HEIGHT_UPDATE = ['production', 'golan-production'].includes(stageConfig.stage) ? 300 : 350;

const UPDATE_ALL_ACCOUNTS_CARD_LABEL = {
  prefix: 'אני רוצה לעדכן את הכרטיס בכל החשבונות',
  active: 'הפעילים',
  withoutPaymentMethod: 'ללא אמצעי תשלום',
};

const UPDATE_PAYMENT_STEP_TITLE = 'תשלום קבוע דרך כרטיס אשראי';

const CreditCard = () => {
  const {getPaymentStatusesForBan} = useDebtPaymentBit();
  const {
    selectedBanData,
    paymentAmount,
    paymentActionType,
    paymentFloorData,
    ccToken,
    actions,
    paymentMethodBans,
    payment,
    emvStep2CompletedSuccessfully,
    creditCardError,
    payingEntityLink,
    bitReturnEvent,
    bitIsReady,
    bitPaymentOption,
  } = useDebtPaymentCreditCard();
  const loginData = useSelector((state) => state.authentication.loginData);
  const bansForUpdate = useSelector((state) => state.debtAndUpdatePayment.selectedBansForPaymentUpdate);
  const allBans = useSelector(selectServerData);
  const epiData = useSelector(selectEpiData);
  const isEmvStep2Pending = useSelector((state) => state.PaymentCG.isEmvStep2Pending);
  const selectedBansForUpdate = useSelectedBansForUpdatePayment();
  const isDebtPaymentFlow = paymentActionType === paymentAction.PAYMENT || paymentActionType === paymentAction.PAYMENT_AND_UPDATE;
  const isUpdatePaymentMethodFlow = paymentActionType === paymentAction.UPDATE_PAYMENT_METHOD;
  const prevEmvPendingRef = useRef(false);

  const getUpdatePaymentMethodReportData = (overrides = {}) => ({
    banData: selectedBanData,
    loginData,
    allBans,
    selectedBanNumbers: bansForUpdate,
    lastPaymentTypeValidation: epiData?.lastPaymentTypeValidation,
    ...overrides,
  });

  const getCreditCardReportData = () => ({
    banData: selectedBanData,
    loginData,
    paymentAmount,
    paymentActionType,
    bansForUpdate,
    actionType: DwhActionType.CreditCardPaymentDetailsPage,
  });

  useEffect(() => {
    if (!isUpdatePaymentMethodFlow) {
      dwhCreditCardPageReport({
        ...getCreditCardReportData(),
        messageText: DWHMessageText.DebtPaymentDetailsPageDisplayed,
        status: DWHStatus.Ongoing,
      });
    } else {
      dwhUpdatePaymentMethodReport({
        ...getUpdatePaymentMethodReportData(),
        actionType: DwhActionType.CreditCardPage,
        messageText: DWHMessageText.CreditCardPageDisplayed,
        status: DWHStatus.Ongoing,
      });
    }
  }, []);

  const [submitted, setSubmitted] = useState(false);
  const [showBitOption, setShowBitOption] = useState(false);
  const [showCallOption, setShowCallOption] = useState(false);

  useEffect(() => {
    if (isUpdatePaymentMethodFlow) {
      return;
    }
    if (creditCardError != null) {
      dwhCreditCardPageReport({
        ...getCreditCardReportData(),
        messageText: DWHMessageText.PaymentDetailsFailedCGError,
        status: DWHStatus.Error,
      });
      if (selectedBanData.isEnergy) {
        setShowCallOption(true);
      } else {
        if (bitPaymentOption) {
          setShowBitOption(true);
        }
      }
    }
    if (emvStep2CompletedSuccessfully) {
      dwhCreditCardPageReport({
        ...getCreditCardReportData(),
        messageText: DWHMessageText.PaymentDetailsCGOK,
        status: DWHStatus.Ongoing,
      });
      setShowBitOption(false);
      setShowCallOption(false);
    }
  }, [creditCardError, emvStep2CompletedSuccessfully]);

  useEffect(() => {
    if (!isUpdatePaymentMethodFlow) {
      return;
    }
    if (creditCardError != null) {
      const errorId = creditCardError.errorId;
      if (errorId === 9 || (errorId === 10 && !ccToken)) {
        dwhUpdatePaymentMethodReport({
          ...getUpdatePaymentMethodReportData(),
          actionType: DwhActionType.CreditCardPage,
          messageText: DWHMessageText.CreditGuardIFrameFailed,
          status: DWHStatus.Ongoing,
        });
      } else {
        dwhUpdatePaymentMethodReport({
          ...getUpdatePaymentMethodReportData(),
          actionType: DwhActionType.CreditGuardValidation,
          messageText: DWHMessageText.CreditGuardValidationFailed,
          status: DWHStatus.Ongoing,
          failureReason: creditCardError.message,
        });
      }
    }
    if (emvStep2CompletedSuccessfully) {
      dwhUpdatePaymentMethodReport({
        ...getUpdatePaymentMethodReportData(),
        actionType: DwhActionType.CreditGuardValidation,
        messageText: DWHMessageText.CreditGuardValidationSucceeded,
        status: DWHStatus.Ongoing,
      });
    }
  }, [creditCardError, emvStep2CompletedSuccessfully]);

  useEffect(() => {
    if (!isUpdatePaymentMethodFlow) {
      return;
    }
    if (isEmvStep2Pending && !prevEmvPendingRef.current) {
      dwhUpdatePaymentMethodReport({
        ...getUpdatePaymentMethodReportData(),
        actionType: DwhActionType.CreditCardPage,
        messageText: DWHMessageText.CreditGuardValidationButtonClicked,
        status: DWHStatus.Ongoing,
      });
    }
    prevEmvPendingRef.current = isEmvStep2Pending;
  }, [isEmvStep2Pending]);

  const handleValidate = () => {
    if (submitted) {
      setSubmitted(false);
    }
  };

  const createAndSendPaymentData = async () => {
    const result = await actions.validatePaymentCG(payment.form);
    if (result.payload.confirmationNumber) {
      actions.setSendPayment();
    }
  };

  function handleSubmitButtonClick() {
    if (isUpdatePaymentMethodFlow) {
      submitUpdatePaymentMethod();
    } else {
      submitPaymentData();
    }
  }
  const submitPaymentData = () => {
    dwhCreditCardPageReport({
      ...getCreditCardReportData(),
      messageText: DWHMessageText.DebtPaymentDetailsButtonClicked,
      status: DWHStatus.Ongoing,
    });
    setSubmitted(true);
    createAndSendPaymentData();
  };

  const submitUpdatePaymentMethod = () => {
    dwhUpdatePaymentMethodReport({
      ...getUpdatePaymentMethodReportData(),
      actionType: DwhActionType.CreditCardPage,
      messageText: DWHMessageText.CreditCardPageButtonClicked,
      status: DWHStatus.Ongoing,
    });
    setSubmitted(true);
    createAndSendPaymentData();
  };

  const getPaymentNumber = () => {
    if (
      Object.keys(paymentFloorData).every(function (k) {
        return paymentFloorData[k];
      })
    ) {
      const price = parseFloat(paymentAmount);
      if (paymentFloorData.firstFloorAmount >= price) {
        return paymentFloorData.firstFloorPayments;
      } else if (paymentFloorData.firstFloorAmount < price && price <= paymentFloorData.secondFloorAmount) {
        return paymentFloorData.secondFloorPayments;
      } else if (paymentFloorData.secondFloorAmount < price && price <= paymentFloorData.thirdFloorAmount) {
        return paymentFloorData.thirdFloorPayments;
      } else return paymentFloorData.fourthFloorPayments;
    }
  };

  useEnterKeyPress(submitPaymentData, !ccToken || showBitOption || showCallOption);

  const getButtonText = () => {
    if (isUpdatePaymentMethodFlow) {
      return 'עדכון וסיום';
    }
    return paymentActionType === paymentAction.PAYMENT_AND_UPDATE ? 'ביצוע תשלום והגדרת כ.אשראי' : 'ביצוע תשלום';
  };

  const bitButtonText = () => {
    return (
      <span className='bit-button-text'>
        <span>לתשלום באמצעות </span>
        <span>
          <Image src={require('../images/bit.svg')} alt='Bit' />
        </span>
      </span>
    );
  };

  const showCheckBox = () => {
    return (
      ccToken &&
      paymentActionType === paymentAction.PAYMENT_AND_UPDATE &&
      !selectedBanData.isEnergy &&
      paymentMethodBans?.paymentIssueBans?.length > 1
    );
  };

  const getPaymentCgType = () => {
    if (isUpdatePaymentMethodFlow) {
      return cgTypeEnum.CHECKCARD;
    }
    return !selectedBanData.isEnergy ? cgTypeEnum.DEBTPAYMENT : cgTypeEnum.DEBTPAYMENTENERGY;
  };

  const getPaymentCgSource = () => {
    if (isUpdatePaymentMethodFlow) {
      return 'DebtAndUpdatePayment';
    }
    return selectedBanData.isEnergy || (paymentActionType === paymentAction.PAYMENT_AND_UPDATE && !selectedBanData.additionalIdNumber)
      ? 'DebtAndUpdatePayment'
      : 'DebtPayment';
  };

  const openUpdateBansDetailsPopup = () => {
    actions.setShowPopup({showPopUp: true, popupType: popupEnum.UPDATE_PAYMENT_BANS_DETAILS});
  };
  const isShowIdNumber = () => {
    if ((paymentActionType === paymentAction.PAYMENT_AND_UPDATE || isUpdatePaymentMethodFlow) && !selectedBanData.additionalIdNumber) {
      return true;
    }

    return false;
  };

  const getIframeHeight = () => {
    if (isUpdatePaymentMethodFlow) {
      return PAYMENT_CG_IFRAME_HEIGHT_UPDATE;
    }
    return PAYMENT_CG_IFRAME_HEIGHT;
  };

  return (
    <div className='debtpayment-step creditcard-step'>
      {isDebtPaymentFlow && (
        <div className='background payment-info'>
          חשבון {selectedBanData.banNumber} | סכום לתשלום: <span className='red'>₪{formatAmount(paymentAmount)}</span>
        </div>
      )}
      {isUpdatePaymentMethodFlow && (
        <>
          <UpdatePaymentBanStrip selectedBans={selectedBansForUpdate} onDetailsClick={openUpdateBansDetailsPopup} />
          <div className={`${isMobile ? 'm-title' : 'l-title'} extra`}>{UPDATE_PAYMENT_STEP_TITLE}</div>
          {payingEntityLink && <CreditCardPayingEntityBanner />}
        </>
      )}

      <div className=' creditcard-component' style={{maxWidth: '440px', width: '100%'}}>
        <PaymentCG
          totalPrice={paymentAmount}
          onValid={handleValidate}
          isHideTermsCheckbox
          padding='0'
          onSubmit={submitted}
          showPaymentCount
          numberOfPayments={!selectedBanData.isEnergy ? getPaymentNumber() : '1'}
          paymentRequestedQuantity={9}
          noLoader
          type={getPaymentCgType()}
          isShowIdNumber={isShowIdNumber()}
          customerId={
            (paymentActionType === paymentAction.PAYMENT_AND_UPDATE || isUpdatePaymentMethodFlow) && !selectedBanData.additionalIdNumber
              ? selectedBanData.idNumber
              : null
          }
          isAllowDebit={isUpdatePaymentMethodFlow ? false : true}
          source={getPaymentCgSource()}
          iframeHeight={getIframeHeight()}
        />
      </div>
      {showCheckBox() && (
        <div className='update-all-check-box-container'>
          <CheckBox
            id={'update-all-check-box'}
            onCheck={(e) => {
              const allBanNumbers = paymentMethodBans.paymentIssueBans?.map((ban) => ban.banNumber);
              const sorted = [selectedBanData.banNumber, ...allBanNumbers.filter((b) => b !== selectedBanData.banNumber)];
              actions.setSelectedBansForPaymentUpdate(sorted);
            }}
            onUncheck={(e) => actions.setSelectedBansForPaymentUpdate(selectedBanData.banNumber)}
            checked={paymentMethodBans.selectedForPaymentUpdate?.length === paymentMethodBans.paymentIssueBans?.length}
            label={
              <>
                <span>
                  {!isMobile ? ' ' : ''}
                  {UPDATE_ALL_ACCOUNTS_CARD_LABEL.prefix}
                  {!isMobile && ` ${UPDATE_ALL_ACCOUNTS_CARD_LABEL.active}`}
                </span>
                <span className='update-all-check-box-popup-container'>
                  <span>
                    {isMobile && `${UPDATE_ALL_ACCOUNTS_CARD_LABEL.active} `}
                    {UPDATE_ALL_ACCOUNTS_CARD_LABEL.withoutPaymentMethod}
                    {isMobile ? ' ' : ''}
                  </span>
                  <span
                    type='button'
                    className='update-all-check-box-popup'
                    onClick={() => {
                      actions.setShowPopup({showPopUp: true, popupType: popupEnum.UPDATE_ALL_CHECK_BOX});
                    }}>
                    איזה חשבונות?
                  </span>
                </span>
              </>
            }
            customClassName='square'
          />
        </div>
      )}
      <div
        className={`continue-button ${!ccToken && !showBitOption && !showCallOption ? 'disabled-btn' : ''} ${
          showBitOption || showCallOption ? 'continue-button--inline-mobile' : ''
        }`}>
        {!showBitOption && !showCallOption && <BtnSubmit text={getButtonText()} onclick={() => handleSubmitButtonClick()} />}
        {showBitOption && (
          <>
            <div className='s-title bit-option-title '>לחילופין, ניתן לשלם את החוב עם bit</div>

            <BtnWhiteBlackBorderBigSize text={bitButtonText()} onclick={() => getPaymentStatusesForBan(selectedBanData.banNumber)} />
          </>
        )}
        {showCallOption && (
          <div className='call-option-container'>
            <div className='s-title-small'> לפרטים נוספים ניתן לפנות לנציג שירות </div>
            <ContactUsAction contentData={isMobile ? contactUsEnergyMob : contactUsEnergyCC} />
          </div>
        )}
      </div>
      <DebtPaymentBitWrapper
        ban={selectedBanData?.banNumber}
        paymentAmount={paymentAmount}
        bitReturnEvent={bitReturnEvent}
        bitIsReady={bitIsReady}
      />
    </div>
  );
};

export default CreditCard;
